import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/river-view.jpg";
import wildlifeImage from "@/assets/gallery/river-turtle.jpg";
import tubingImage from "@/assets/gallery/tubing-canyon-view.jpg";

const PUBLISH_DATE = "May 27, 2026";
const CANONICAL = "/blog/guadalupe-river-facts-guide";
const TITLE =
  "Guadalupe River Facts — Where It Starts, How Deep, Alligators & More | Son's";
const DESCRIPTION =
  "Everything you've wondered about the Guadalupe River in Texas — where it starts and ends, how long, how deep, alligators, flooding, and the best stretch for tubing near New Braunfels.";

const FAQS = [
  {
    q: "Where is the Guadalupe River?",
    a: "The Guadalupe River runs through south-central Texas, flowing roughly southeast from the Texas Hill Country to the Gulf Coast. Its most popular recreational stretch winds through New Braunfels and the surrounding River Road area — that's where most tubing, cabana rentals, and family floats happen.",
  },
  {
    q: "Where does the Guadalupe River start and end?",
    a: "The river begins in western Kerr County, Texas, where its North and South Forks meet near Hunt. From there it travels about 230 miles southeast through Kerrville, Comfort, New Braunfels, Seguin, Gonzales, Cuero, and Victoria before emptying into San Antonio Bay on the Gulf of Mexico.",
  },
  {
    q: "How long is the Guadalupe River?",
    a: "About 230 miles from its headwaters in the Hill Country to San Antonio Bay. The stretch tubers and cabana guests care about — the spring-fed section between Canyon Lake and New Braunfels — is roughly 25 miles long.",
  },
  {
    q: "How deep is the Guadalupe River?",
    a: "Depth varies dramatically. The tubing stretch near New Braunfels is generally 2 to 5 feet deep, with occasional pools up to 10 feet. Deeper holes exist below Canyon Dam and in stretches downstream. The shallow, predictable depth is exactly why the New Braunfels section is so family-friendly.",
  },
  {
    q: "Are there alligators in the Guadalupe River?",
    a: "Not in the tubing section. Alligators live in the lower river and estuary near the Gulf Coast — well downstream of New Braunfels. The Hill Country stretch where you'll be floating is clear, cool, spring-fed water with turtles, fish, and herons — no gators.",
  },
  {
    q: "Why does the Guadalupe River flood?",
    a: "The Texas Hill Country has steep terrain and thin soil. Heavy rain runs off fast, and the Guadalupe can rise quickly during storms. Canyon Dam, built in 1964, regulates flow on the most-tubed stretch, which is part of why this section is so reliably floatable in summer.",
  },
  {
    q: "Is the Guadalupe River clean?",
    a: "Yes — especially the upper/middle stretch near New Braunfels. The water is spring-fed, clear enough to see the bottom in most spots, and runs cool (around 70°F) even in August.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Guadalupe River Facts — Where It Starts, How Deep, Alligators & More",
          item: `https://sonsguadalupe.com${CANONICAL}`,
        },
      ],
    },
    {
      "@type": "BlogPosting",
      headline:
        "Guadalupe River Facts — Where It Starts, How Deep, Alligators & More",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-27",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "guadalupe river facts, where does the guadalupe river start, how long is the guadalupe river, how deep is the guadalupe river, alligators in the guadalupe river, guadalupe river texas, guadalupe river new braunfels",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogGuadalupeRiverFacts = () => {
  const related = getRelatedPosts("guadalupe-river-facts-guide", 3);
  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        schemaJson={schemaJson}
      />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async"
            src={heroImage}
            alt="The Guadalupe River winding through the Texas Hill Country near New Braunfels"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Guadalupe River Facts — Where It Starts, How Deep, Alligators &amp; More
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                The <strong>Guadalupe River</strong> is one of the most-loved rivers in
                Texas — and one of the most-Googled. Before your first float, here's
                everything you've probably wondered: where it starts, how long it is,
                how deep, whether there are alligators (spoiler: no — not where you'll
                be tubing), and why the New Braunfels stretch is the best in the state
                for families.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Where is the Guadalupe River?
              </h2>
              <p>
                The Guadalupe River runs through south-central Texas, flowing roughly
                southeast from the Texas Hill Country to the Gulf Coast. It passes
                through Kerrville, Comfort, Canyon Lake, New Braunfels, Seguin,
                Gonzales, Cuero, and Victoria before emptying into San Antonio Bay.
              </p>
              <p>
                The stretch most visitors care about — the cool, spring-fed,
                family-friendly tubing water — runs between Canyon Lake and New
                Braunfels along the legendary <Link to="/blog/river-road-new-braunfels-guide" className="text-primary hover:underline">River Road corridor</Link>.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Where Does the Guadalupe River Start and End?
              </h2>
              <p>
                The river starts in western Kerr County, where its North and South
                Forks meet near the town of Hunt, Texas. From the headwaters it
                travels about <strong>230 miles southeast</strong> and ends at San
                Antonio Bay on the Gulf of Mexico — making it one of the longest
                rivers entirely within the state.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                How Long Is the Guadalupe River?
              </h2>
              <p>
                Roughly <strong>230 miles</strong> from headwaters to the Gulf. But
                the part you'll actually float? The tubing stretch between Canyon
                Lake and New Braunfels is about <strong>25 miles long</strong>, and
                most family floats cover just 1–3 miles of it on a relaxed afternoon.
              </p>

              <img
                src={tubingImage}
                alt="Tubing the Guadalupe River through the Hill Country canyon"
                loading="lazy"
                className="w-full rounded-2xl shadow-elevated my-8"
              />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                How Deep Is the Guadalupe River?
              </h2>
              <p>
                Depth varies by stretch. On the popular tubing section near New
                Braunfels, the river is generally <strong>2 to 5 feet deep</strong>,
                with the occasional deeper pool up to 10 feet around bends and below
                small falls. Below Canyon Dam and farther downstream you'll find
                deeper holes used for fishing and kayaking.
              </p>
              <p>
                That shallow, predictable depth is exactly why the New Braunfels
                stretch is <Link to="/blog/tubing-with-kids-guadalupe-river" className="text-primary hover:underline">so family-friendly for tubing with kids</Link>.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Are There Alligators in the Guadalupe River?
              </h2>
              <p>
                <strong>No — not in the tubing section.</strong> Alligators live in
                the lower Guadalupe and the estuary near the Gulf Coast, hundreds of
                miles downstream of New Braunfels. The Hill Country stretch where
                you'll float is clear, cool, spring-fed water. You'll see turtles,
                herons, perch, bass, and the occasional river otter — no gators.
              </p>

              <img
                src={wildlifeImage}
                alt="A turtle resting on a rock in the Guadalupe River"
                loading="lazy"
                className="w-full rounded-2xl shadow-elevated my-8"
              />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Why Does the Guadalupe River Flood?
              </h2>
              <p>
                The Texas Hill Country has steep terrain, limestone bedrock, and thin
                soil — heavy rainfall runs off fast instead of soaking in. That can
                cause the Guadalupe to rise quickly during major storms.{" "}
                <strong>Canyon Dam</strong>, completed in 1964, regulates flow on the
                most-tubed stretch, which is part of why the New Braunfels section
                stays so reliably floatable through the summer.
              </p>
              <p>
                For real-time flow before you go, check our{" "}
                <Link to="/blog/guadalupe-river-water-levels-safety" className="text-primary hover:underline">
                  Guadalupe River water levels & safety guide
                </Link>
                .
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                What Wildlife Will You See?
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Turtles</strong> sunning on rocks and logs</li>
                <li><strong>Great blue herons</strong> and kingfishers fishing the shallows</li>
                <li><strong>Largemouth and Guadalupe bass</strong> (the official state fish)</li>
                <li>Sunfish, perch, and catfish</li>
                <li>Occasional <strong>river otters</strong> at dawn and dusk</li>
                <li>Deer and the occasional fox along the banks</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Is the Water Clean?
              </h2>
              <p>
                Very. The upper and middle Guadalupe is spring-fed — clear enough to
                see your toes in most spots, and cool (around 70°F) even in August
                thanks to releases from the bottom of Canyon Lake.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                The Best Stretch for Tubing
              </h2>
              <p>
                If you're picking one section to experience, make it the spring-fed
                run between Canyon Lake and New Braunfels — and ideally float it from
                a private river park instead of a public access point. That's what
                we do at{" "}
                <Link to="/cabanas" className="text-primary hover:underline">
                  Son's Guadalupe
                </Link>
                : private river frontage, shaded cabanas as your basecamp, tube
                rentals, parking, and an easy walk to the water.
              </p>
              <p>
                See{" "}
                <Link to="/blog/best-time-to-tube-guadalupe-river" className="text-primary hover:underline">
                  the best time to tube the Guadalupe
                </Link>{" "}
                for a month-by-month breakdown of crowds, flow, and weather.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Guadalupe River FAQs
              </h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      {f.q}
                    </h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now">
                  <Button variant="default" size="lg" className="text-lg px-10 py-6">
                    Book Your Float
                  </Button>
                </Link>
                <Link to="/tubing">
                  <Button variant="outline" size="lg" className="text-lg px-10 py-6">
                    Tubing Info
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />
      </main>
      <SEOContentBlock
        eyebrow="Plan Your Guadalupe River Day"
        heading="Now You Know the River — Come Float It"
        paragraphs={[
          "The best way to experience the Guadalupe River is from a private cabana on the spring-fed New Braunfels stretch. Son's Guadalupe handles parking, river access, tube rentals, and your basecamp so you can focus on the float.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Best Time to Tube", to: "/blog/best-time-to-tube-guadalupe-river" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogGuadalupeRiverFacts;
